/**
 * =============================================================================
 * Componente: VariablePicker
 * Versão: 1.1.0
 *
 * Release notes:
 *   v1.0.0 - Versão inicial (abas + busca).
 *   v1.1.0 - Suporte a allowedGroups/allowedTokens para filtrar catálogo
 *            (usado no picker do TÍTULO do step).

 *
 * Descrição:
 *   Picker organizado em abas para inserção de variáveis dinâmicas no
 *   roteiro/script de um step de playbook. Substitui a antiga lista plana
 *   de chips por uma navegação por grupo (Contato, Negócio, Produtos,
 *   Contrato, Financeiro, Vendedor, Empresa, Tempo) + busca textual.
 *
 *   - Cada chip mostra o token e abre tooltip com label + exemplo.
 *   - Variáveis recém-adicionadas exibem badge "Novo".
 *   - Suporta filtragem por busca em todas as abas simultaneamente.
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { Search, Variable } from 'lucide-react';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import {
  AVAILABLE_VARIABLES,
  VARIABLE_GROUP_ORDER,
  type AvailableVariable,
  type VariableGroup,
} from '@/lib/playbookScript';

interface VariablePickerProps {
  onInsert: (token: string) => void;
  /**
   * [v1.1.0] Restringe o catálogo apenas a variáveis destes grupos.
   * Útil no picker do TÍTULO do step (subset menor que o roteiro completo).
   */
  allowedGroups?: VariableGroup[];
  /**
   * [v1.1.0] Restringe o catálogo apenas a variáveis com estes tokens.
   * Combinado com `allowedGroups`, aplica-se ambas as restrições.
   */
  allowedTokens?: string[];
}

export function VariablePicker({ onInsert, allowedGroups, allowedTokens }: VariablePickerProps) {
  const [search, setSearch] = useState('');

  // [v1.1.0] Catálogo filtrado por grupos/tokens permitidos (aditivo)
  const catalog = useMemo(() => {
    let list = AVAILABLE_VARIABLES;
    if (allowedGroups && allowedGroups.length > 0) {
      const set = new Set(allowedGroups);
      list = list.filter((v) => set.has(v.group));
    }
    if (allowedTokens && allowedTokens.length > 0) {
      const set = new Set(allowedTokens);
      list = list.filter((v) => set.has(v.token));
    }
    return list;
  }, [allowedGroups, allowedTokens]);

  // [v1.1.0] Aba ativa: primeira aba que tenha itens após filtro
  const availableGroups = useMemo(
    () => VARIABLE_GROUP_ORDER.filter((g) => catalog.some((v) => v.group === g)),
    [catalog],
  );
  const [activeTab, setActiveTab] = useState<VariableGroup>(availableGroups[0] ?? 'Contato');

  // Filtra por termo (token ou label) em todas as abas
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return catalog;
    return catalog.filter(
      (v) =>
        v.token.toLowerCase().includes(term) ||
        v.label.toLowerCase().includes(term),
    );
  }, [search, catalog]);

  // Agrupa por aba
  const byGroup = useMemo(() => {
    const map = new Map<VariableGroup, AvailableVariable[]>();
    for (const g of availableGroups) map.set(g, []);
    for (const v of filtered) {
      if (map.has(v.group)) map.get(v.group)?.push(v);
    }
    return map;
  }, [filtered, availableGroups]);


  const isSearching = search.trim().length > 0;

  // Quando houver busca, mostra um único bloco com todos os matches
  if (isSearching) {
    return (
      <div className="rounded-md border bg-muted/30 p-2 space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
            <Variable className="h-3 w-3" /> Variáveis
          </div>
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar variável..."
              className="h-7 pl-7 text-xs"
            />
          </div>
        </div>
        <VariableChipList items={filtered} onInsert={onInsert} />
        {filtered.length === 0 && (
          <p className="text-[11px] text-muted-foreground text-center py-2">
            Nenhuma variável encontrada para "{search}".
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-muted/30 p-2 space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
          <Variable className="h-3 w-3" /> Variáveis (clique para inserir)
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar variável..."
            className="h-7 pl-7 text-xs"
          />
        </div>
      </div>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as VariableGroup)}>
        {/* Abas com scroll horizontal em telas pequenas */}
        <ScrollArea className="w-full">
          <TabsList className="h-8 inline-flex w-max">
            {availableGroups.map((g) => {
              const count = byGroup.get(g)?.length ?? 0;
              return (

                <TabsTrigger
                  key={g}
                  value={g}
                  className="text-[11px] h-7 px-2 gap-1"
                >
                  {g}
                  <span className="text-[10px] text-muted-foreground">{count}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
          <ScrollBar orientation="horizontal" className="h-1.5" />
        </ScrollArea>

        {availableGroups.map((g) => (
          <TabsContent key={g} value={g} className="mt-2">
            <VariableChipList items={byGroup.get(g) ?? []} onInsert={onInsert} />
          </TabsContent>
        ))}

      </Tabs>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Lista de chips reutilizável (modo busca + por aba)
// ----------------------------------------------------------------------------
function VariableChipList({
  items,
  onInsert,
}: {
  items: AvailableVariable[];
  onInsert: (token: string) => void;
}) {
  if (items.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground text-center py-2">
        Nenhuma variável neste grupo.
      </p>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-wrap gap-1">
        {items.map((v) => (
          <Tooltip key={v.token}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 text-[11px] px-2 gap-1 relative"
                onClick={() => onInsert(v.token)}
              >
                {`{{${v.token}}}`}
                {v.isNew && (
                  <Badge
                    variant="secondary"
                    className="h-3.5 px-1 text-[8px] leading-none bg-primary/15 text-primary"
                  >
                    novo
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="font-medium text-xs">{v.label}</p>
              {v.example && (
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Ex.: {v.example}
                </p>
              )}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
